import {stringify} from "comment-json";
import {omit, startCase} from "lodash";
import {NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {getNodeByIdOrFQCN} from "../../../cody-wizard/node/get-node-by-id-or-fqcn";
import {
  convertJsonSchemaToShorthandIfPossible
} from "../../../json/schema/convert-json-schema-to-shorthand-if-possible";
import {nodeLabel, removeDefaultService} from "../../default-service";
import {ProophBoardDescriptionKeys} from "../../descriptions";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PlayEventRuntimeInfo} from "./event-definitions";
import {prepareDiffEvent} from "./prepare-diff-event";

export class EventMetadataChanged implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'EventMetadataChanged';
  public readonly operation: PlayshotChangedOperation = 'changed';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.event;
  public readonly diff: PlayshotChangedDiff;

  private changedEvent: PlayEventRuntimeInfo;

  constructor(name: string, nodeId: string, event: PlayEventRuntimeInfo, newPlayshot: Playshot, oldPlayshot: Playshot) {

    // @TODO: handle aggregate event
    const newApplyRules = undefined; /* = playshot.playConfig.eventReducers[event.desc.name]; */
    const oldApplyRules = undefined;

    let oldEvent = oldPlayshot.playConfig.events[event.desc.name];

    if(!oldEvent) {
      oldEvent = event;
    }

    this.name = name;
    this.nodeId = nodeId;
    this.diff = {
      new: prepareDiffEvent(event, newApplyRules),
      old: prepareDiffEvent(oldEvent, oldApplyRules),
    };
    this.changedEvent = event;
  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = getNodeByIdOrFQCN(this.nodeId, this.name, NodeType.event, graph, elementsTree);

    if(!existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the event cannot be found on the board.`,
        details: `There is no element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }

    // @TODO: handle aggregate event
    const newApplyRules = undefined; /* = playshot.playConfig.eventReducers[event.desc.name]; */

    graph.changeNodeMetadata(existingNode, stringify(omit({
      ...this.changedEvent.desc,
      schema: convertJsonSchemaToShorthandIfPossible(this.changedEvent.schema),
      // @TODO: handle factory
    }, [
      "name",
      ...ProophBoardDescriptionKeys
    ])))

    graph.focusNode(existingNode);

    return makeAsyncIterable({
      cody: `I've changed event metadata of "${nodeLabel(this.name)}".`
    });
  }
}
